import { Dispatch, SetStateAction } from 'react';
import { TRANSACTIONS_ROUTE_CANDIDATE } from '@lib/actions/api/wallet/tags';
import { ApiOrderSaveOrder } from '@type/v1-api-types/order';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import {
  useSwrMyBestieCheckout,
  useSwrMyBestieActivate,
} from '@hooks/useSwrApi/endpoints/my-bestie';
import {
  useSwrOrderContinueCandidate,
} from '@hooks/useSwrApi/endpoints/order';
import { useSwrGlobal } from '@hooks/useSwrApi';

interface UseBuyMyBestieProps {
  stepSetter?: Dispatch<SetStateAction<string>>;
  successCallbackFunction?: () => void;
}

/**
 * @description Hook to manage the My Bestie purchase flow.
 * 1. Fetches cart/preview details from /v1/user-order/preview (reuses the candidate order preview).
 * 2. Creates a Stripe checkout via POST /v1/my-bestie.
 * 3. On successful payment, activates the service via POST /v1/my-bestie/activate.
 */
export const useBuyMyBestie = (props?: UseBuyMyBestieProps) => {
  const { stepSetter, successCallbackFunction } = props || {};

  const { locale, pathname, router } = useExtendedRouter();
  const { revalidateKey } = useSwrGlobal();

  const handleOrderError = () => {
    if (stepSetter) stepSetter('Error');
  };

  const handleActivateSuccess = () => {
    if (stepSetter) stepSetter('Activated');
    if (successCallbackFunction) {
      setTimeout(() => { successCallbackFunction(); }, 3000);
    }
    setTimeout(() => revalidateKey('header-info'), 3000);
  };

  const handleActivateError = () => {
    if (stepSetter) stepSetter('Error');
  };

  // SWR: Activate My Bestie from wallet balance
  const {
    trigger: activateBestie,
    isMutating: activating,
  } = useSwrMyBestieActivate({
    locale,
    successCallback: handleActivateSuccess,
    errorCallback: handleActivateError,
  });

  const handleOrderSuccess = async (data: ApiOrderSaveOrder) => {
    let step: string | undefined;

    if (data?.completed) {
      if (data?.paid) {
        // Payment completed via card — now activate the service
        await activateBestie({});
        return;
      }
      step = 'CompletedInvoice';
      if (pathname && pathname.startsWith(TRANSACTIONS_ROUTE_CANDIDATE)) router.refresh();
      setTimeout(() => revalidateKey('header-info'), 5000);
    } else if (data?.checkoutSessionUrl) {
      window.location.replace(data.checkoutSessionUrl);
      return;
    } else {
      step = 'NotCompleted';
    }

    if (stepSetter) stepSetter(step);
  };

  // SWR: Get order preview details (reuses candidate order preview)
  const {
    data: cartDetails,
    isLoading: loadingCartDetails,
  } = useSwrOrderContinueCandidate({ locale });

  // SWR: Create checkout session for My Bestie
  const {
    trigger: orderCredits,
    data: orderResponse,
    isMutating: savingOrder,
  } = useSwrMyBestieCheckout({
    locale,
    successCallback: handleOrderSuccess,
    errorCallback: handleOrderError,
  });

  return {
    cartDetails,
    loadingCartDetails,
    orderCredits,
    orderResponse,
    savingOrder,
    activating,
    activateBestie,
    paths: {
      toInvoice: orderResponse && orderResponse?.completed && orderResponse?.paid === false && orderResponse?.orderId
        ? () => router.push(`/wallet/order/${orderResponse.orderId}`)
        : undefined,
      notCompleted: orderResponse && orderResponse?.completed === false && orderResponse?.orderId
        ? () => router.push(`/order/checkout/${orderResponse.orderId}`)
        : undefined,
    },
  };
};
